import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, Paperclip, Archive, ArchiveRestore, Ban, CircleCheck, FileText } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ChatView({ convRef }) {
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const convId = conv?.conversation_id;

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/conversations/resolve/${convRef}`);
      setConv(res.data);
      const msgs = await api.get(`/conversations/${res.data.conversation_id}/messages`);
      setMessages(msgs.data);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [convRef]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !convId) return;
    setSending(true);
    try {
      await api.post(`/conversations/${convId}/messages`, { text });
      setText("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const attach = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !convId) return;
    setSending(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post(`/conversations/${convId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post(`/conversations/${convId}/messages`, {
        text: text.trim(),
        attachment_file_id: up.data.file_id,
      });
      setText("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const toggle = async (action) => {
    setError("");
    try {
      await api.post(`/conversations/${convId}/${action}`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (!conv) {
    return (
      <div className="flex-1 flex items-center justify-center py-16">
        {error ? (
          <p data-testid="chat-error" className="text-sm text-red-600">{error}</p>
        ) : (
          <div className="w-8 h-8 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
        )}
      </div>
    );
  }

  const blocked = conv.blocked;

  return (
    <div data-testid="chat-view" className="flex-1 flex flex-col">
      <div className="bg-white rounded-t-xl border border-slate-200 px-5 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p data-testid="chat-title" className="font-semibold text-brand-navy text-sm truncate">{conv.display_name}</p>
          <p className="text-xs text-slate-400 truncate">
            {conv.participants.map((p) => p.name).join(", ")}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            data-testid="chat-archive-button"
            onClick={() => toggle("archive")}
            title={conv.archived ? "Désarchiver" : "Archiver"}
            className="p-2 text-slate-400 hover:text-brand-navy transition-colors"
          >
            {conv.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
          </button>
          <button
            data-testid="chat-block-button"
            onClick={() => toggle("block")}
            title={conv.blocked_by_me ? "Débloquer" : "Bloquer"}
            className={`p-2 transition-colors ${conv.blocked_by_me ? "text-red-600" : "text-slate-400 hover:text-red-600"}`}
          >
            {conv.blocked_by_me ? <CircleCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {blocked && (
        <div data-testid="chat-blocked-notice" className="bg-red-50 border-x border-red-200 px-5 py-2 text-xs text-red-700">
          Cette conversation est bloquée. Aucun message ne peut être envoyé.
        </div>
      )}
      {error && (
        <div data-testid="messages-error" className="bg-red-50 border-x border-red-200 px-5 py-2 text-xs text-red-700">{error}</div>
      )}

      <div data-testid="messages-thread" className="flex-1 bg-white border-x border-slate-200 p-5 min-h-[300px] max-h-[55vh] overflow-y-auto space-y-3">
        {messages.map((m) => {
          const mine = m.sender_id === user.user_id;
          return (
            <div key={m.message_id} data-testid={`message-${m.message_id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-brand-navy text-white" : "bg-slate-100 text-brand-navy"}`}>
                <p className={`text-[11px] mb-0.5 ${mine ? "text-white/60" : "text-slate-400"}`}>
                  {m.sender_name} · {m.created_at ? new Date(m.created_at).toLocaleString("fr-CA") : ""}
                </p>
                {m.text && <p className="text-sm whitespace-pre-line">{m.text}</p>}
                {m.attachment && (
                  <a
                    data-testid={`attachment-${m.message_id}`}
                    href={`${API_BASE}/api/files/${m.attachment.file_id}/download`}
                    className={`mt-1 inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-3 py-1.5 ${mine ? "bg-white/15 text-white hover:bg-white/25" : "bg-white text-brand-blue border border-slate-200 hover:bg-slate-50"}`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    {m.attachment.name}
                  </a>
                )}
              </div>
            </div>
          );
        })}
        {messages.length === 0 && (
          <p data-testid="messages-empty" className="text-sm text-slate-400 text-center py-12">
            Aucun message. Écrivez le premier message ci-dessous.
          </p>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="flex gap-2 bg-white rounded-b-xl border border-slate-200 p-3">
        <input
          ref={fileInputRef}
          data-testid="attachment-input"
          type="file"
          className="hidden"
          onChange={attach}
        />
        <button
          type="button"
          data-testid="attachment-button"
          onClick={() => fileInputRef.current?.click()}
          disabled={blocked || sending}
          title="Joindre un fichier (max 10 Mo)"
          className="p-3 text-slate-400 hover:text-brand-navy transition-colors disabled:opacity-40"
        >
          <Paperclip className="w-4 h-4" />
        </button>
        <input
          data-testid="message-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={blocked ? "Conversation bloquée" : "Écrivez votre message…"}
          disabled={blocked}
          className="flex-1 border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent disabled:bg-slate-50"
        />
        <button
          data-testid="message-send-button"
          type="submit"
          disabled={blocked || sending || !text.trim()}
          className="bg-brand-turquoise text-white font-semibold rounded-lg px-5 py-3 hover:bg-brand-navy transition-colors duration-200 disabled:opacity-40"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
